import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const TransientProps = ['isDisabled', 'isLeftComp', 'isRightComp', 'isNavbar', 'withoutBackground', 'padding'];

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(({ theme, ...props }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: props.cursor,
  padding: props.padding ?? (props.withoutBackground ? '0' : '12px'),
  borderRadius: '8px',
  opacity: props.isDisabled ? '0.5' : '1',
  minHeight: props.minHeight ?? 'auto',
  '&:hover': {
    background: props.isDisabled || props.withoutBackground ? 'unset' : theme?.colors?.functional?.E6F1FD,
  },
  '&:active, &.active-styles':
    props.isDisabled || props.withoutBackground
      ? {}
      : props.isNavbar
        ? {
            background: theme?.colors?.functional?.E6F1FD,
            '& p': {
              color: theme.colors.button.blue.default,
            },
            '.left-item': {
              background: theme.colors.button.blue.default,
              '& svg': {
                color: theme.colors.common.white,
              },
            },
          }
        : {
            background: checkNewNavEnabled() ? theme.colors.functional.CAE8F0 : theme.colors.functional.E6F1FD,
            '& p': {
              color: theme.colors.button.blue.default,
            },
            '& svg': {
              color: theme.colors.button.blue.default,
            },
          },
  // '&:active &.active-styles, .left-item': props.isNavbar
  //   ? {
  //       background: theme.colors.button.blue.default,
  //       '& svg': {
  //         color: theme.colors.common.white
  //       }
  //     }
  //   : {}
}));

const ComponentWrapper = styled('div', {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(({ theme, ...props }) => ({
  flex: '0 0 auto',
  background: props.isNavbar ? theme.colors.neutral['666666'] : 'unset',
  borderRadius: '8px',
  padding: props.isNavbar ? '6px' : '0px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& svg': props.isNavbar
    ? {
        color: theme.colors.common.white,
      }
    : {},
  '& > :first-child': props.isNavbar
    ? {
        height: '16px !important',
        width: '16px !important',
      }
    : {
        height: '100% !important',
        width: '100% !important',
      },
}));

const StyledTextBodyMedium = styled(TextBodyMedium)`
  overflow: hidden;
  white-space: ${(props) => (props.wrapText ? 'normal' : 'nowrap')};
  text-overflow: ellipsis;
`;

const StyledTextLabel = styled(TextLabel)`
  overflow: hidden;
  white-space: ${(props) => (props.wrapText ? 'normal' : 'nowrap')};
  text-overflow: ellipsis;
`;

const LIST_ITEM_SIZE = {
  extraSmall: 'extraSmall', // 12px
  small: 'small', // 16px
  regularSmall: 'regularSmall', // 14px
  regular: 'regular', // 24px
  large: 'large', // 32px
  extraLarge: 'extraLarge', // 40px
};

const SIDE_COMPS_SIZE = {
  extraSmall: { height: '12px', width: '12px' }, // 12px
  small: { height: '16px', width: '16px' }, // 16px
  regularSmall: { height: '24px', width: '24px' }, // 24px
  regular: { height: '24px', width: '24px' }, // 24px
  large: { height: '32px', width: '32px' }, // 32px
  extraLarge: { height: '40px', width: '40px' }, // 40px
};

export const ListItemComp = ({
  RightComponent = null,
  LeftComponent = null,
  className = '',
  subTitle = null,
  subSubtitle = null,
  title,
  onClick = () => {},
  isDisabled = false,
  active = false,
  size = LIST_ITEM_SIZE.regular,
  isNavbar = false,
  isBoldText = true,
  withoutBackground = false,
  wrapText = false,
  ...props
}) => {
  const theme = useTheme();

  const isLeftComp = !!LeftComponent;
  const isRightComp = !!RightComponent;

  let sideComponentsSize = SIDE_COMPS_SIZE[size];

  if (size && Object.keys(LIST_ITEM_SIZE).includes(size) && !isNavbar) {
    sideComponentsSize = SIDE_COMPS_SIZE[size];
  }
  if (size && !Object.keys(LIST_ITEM_SIZE).includes(size) && !isNavbar) {
    sideComponentsSize = SIDE_COMPS_SIZE.regular;
    console.error(`Error: Invalid list item size : ${size} . Pick one from [${Object.keys(LIST_ITEM_SIZE)}]`);
  }
  if (isNavbar) {
    sideComponentsSize = SIDE_COMPS_SIZE.regular;
  }

  const renderLeftComponent = () => {
    if (isLeftComp) {
      return (
        <ComponentWrapper isNavbar={isNavbar} className={isNavbar ? 'left-item' : ''} style={sideComponentsSize}>
          <LeftComponent />
        </ComponentWrapper>
      );
    }
  };

  const renderSubtitle = () => {
    if (subTitle || subSubtitle) {
      return (
        <Flex flexDir="column">
          {subTitle && <StyledTextLabel color={theme.colors.text.g2}>{subTitle}</StyledTextLabel>}
          {subSubtitle &&
            (Array.isArray(subSubtitle) ? (
              subSubtitle.map((item) => <StyledTextLabel color={theme.colors.text.g2}>{item}</StyledTextLabel>)
            ) : (
              <StyledTextLabel color={theme.colors.text.g2}>{subSubtitle}</StyledTextLabel>
            ))}
        </Flex>
      );
    }
  };

  const renderTitle = ({ isNavbar = false }) => {
    if (title) {
      if (isNavbar || size === LIST_ITEM_SIZE.regularSmall) {
        return (
          <StyledTextBodyMedium bold={isBoldText} color={theme.colors.text.g1} wrapText={wrapText}>
            {title}
          </StyledTextBodyMedium>
        );
      }
      if (size === LIST_ITEM_SIZE.small) {
        return (
          <StyledTextBodyMedium bold={isBoldText} color={theme.colors.text.g1} wrapText={wrapText}>
            {title}
          </StyledTextBodyMedium>
        );
      }
      return (
        <StyledTextBodyMedium bold={isBoldText} color={theme.colors.text.g1} wrapText={wrapText}>
          {title}
        </StyledTextBodyMedium>
      );
    }
  };

  const renderOtherInfo = ({ isNavbar = false }) => (
    <Box
      ml="8px"
      mr={5}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      width="100%"
      sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} // <- to truncate text
      className="other-info"
    >
      {renderTitle({ isNavbar })}

      {renderSubtitle()}
    </Box>
  );

  const handleOnClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const renderActionItems = () => {
    if (isDisabled) {
      //   return <MeshIcon name={'disabledIcon'} />;
    }
    if (isRightComp) {
      return <ComponentWrapper style={sideComponentsSize}>{RightComponent}</ComponentWrapper>;
    }
  };

  return (
    <Wrapper
      isDisabled={isDisabled}
      className={active ? `${className} active-styles` : className}
      onClick={handleOnClick}
      isLeftComp={isLeftComp}
      isRightComp={isRightComp}
      isNavbar={isNavbar}
      withoutBackground={withoutBackground}
      cursor={onClick ? (isDisabled ? 'not-allowed' : 'pointer') : 'inherit'}
      {...props}
    >
      <Box
        style={{ marginLeft: 0, alignItems: 'center' }}
        display="flex"
        alignItems={subTitle ? 'unset' : 'center'}
        width="100%"
      >
        {renderLeftComponent()}

        {renderOtherInfo({ isNavbar })}
      </Box>

      {renderActionItems()}
    </Wrapper>
  );
};
